import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "./api/apiFunctions";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [form, setForm] = useState({ password: "", confirm: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Invalid reset link. Please request a new one.");
      return;
    }
    if (!form.password || form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(token, form.password);
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Invalid or expired reset link. Please request a new one.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f7f8fa] to-[#e6e9f5] py-5">
      <div className="w-11/12 sm:w-full sm:max-w-md bg-white rounded-2xl shadow-xl p-10 border border-[#e5e7eb]">
        {!success ? (
          <>
            <h2 className="text-3xl font-extrabold text-[#2d348f] mb-2 text-center tracking-tight">
              Set New Password
            </h2>
            <p className="text-sm text-[#837f90] text-center mb-8">
              Choose a strong password for your SAL Mine account.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="relative flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#837f90]">New Password</label>
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="Min. 8 characters"
                  className="border-0 border-b-2 border-[#4962d8] focus:outline-none text-sm py-1 bg-transparent text-black font-medium pr-10 disabled:opacity-50"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#4962d8] focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"} text-lg`}></i>
                </button>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#837f90]">Confirm New Password</label>
                <input
                  name="confirm"
                  type={showPassword ? "text" : "password"}
                  value={form.confirm}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="Re-enter password"
                  className="border-0 border-b-2 border-[#4962d8] focus:outline-none text-sm py-1 bg-transparent text-black font-medium disabled:opacity-50"
                />
              </div>
              {error && <div className="text-red-500 text-sm">*{error}</div>}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 font-bold text-white text-base bg-gradient-to-r from-[#445eed] to-[#2d348f] hover:from-[#3916ab] hover:to-[#4962d8] transition-colors rounded-lg shadow-md mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Resetting...
                  </div>
                ) : (
                  "Reset Password"
                )}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-2xl font-extrabold text-[#2d348f] mb-3">Password Reset!</h2>
            <p className="text-sm text-[#837f90] mb-8 leading-relaxed">
              Your password has been updated successfully. You can now log in with your new password.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="w-full py-3 font-bold text-white text-base bg-gradient-to-r from-[#445eed] to-[#2d348f] hover:from-[#3916ab] hover:to-[#4962d8] transition-colors rounded-lg shadow-md"
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
